/**
 * Debt payoff maths - w2_17.
 *
 * Month by month:
 *   1. Each debt grows by its interest: what's left x (yearly rate / 12).
 *   2. Each debt gets its minimum payment (or whatever's left, if less).
 *   3. Your extra goes to the debt at the front of the line:
 *        avalanche - the highest interest rate first
 *        snowball  - the smallest balance first
 *      If the front one is cleared, the rest of the extra moves to the next.
 *   4. When a debt is paid off, its minimum is freed. It moves to the next
 *      debt too, unless you said not to (rollover[id] === false).
 *
 * A debt's date is the month it reaches $0. Total interest is all the
 * interest added on the way. Dates are estimates: lenders work interest out
 * daily and round differently, so a real date can be a month either way.
 */

const n = (v) => { const x = Number(v); return Number.isFinite(x) ? x : 0; };
export const MAX_MONTHS = 600; // 50 years: anything longer is "not at this rate"

export const orderFor = (method) => (method === 'snowball'
  ? (a, b) => (a.b - b.b) || (b.r - a.r)
  : (a, b) => (b.r - a.r) || (a.b - b.b));

/**
 * debts: [{ id, balance, rate (yearly %, e.g. 6.9), min }]
 * extra: extra each month on top of the minimums
 * rollover: { [id]: false } for debts whose freed minimum should NOT move on
 * Returns { months: { [id]: monthIndex | null }, totalMonths, interest,
 *   interestBy: { [id] }, firstMonthPay: { [id] }, path: [total owed after each month],
 *   payments: [{ [id]: amount }] per month (first 60), stuck: bool }
 */
export function simulate({ debts = [], extra = 0, method = 'avalanche', rollover = {} }) {
  const ds = debts
    .filter((d) => n(d.balance) > 0.004)
    .map((d) => ({ id: d.id, b: n(d.balance), r: Math.max(0, n(d.rate)) / 100 / 12, min: Math.max(0, n(d.min)) }));
  const months = {};
  const interestBy = {};
  debts.forEach((d) => { months[d.id] = n(d.balance) > 0.004 ? null : 0; interestBy[d.id] = 0; });
  const path = [];
  const payments = [];
  let interest = 0;
  let freed = 0;
  let m = 0;
  const sorter = orderFor(method);
  while (ds.some((d) => d.b > 0.004) && m < MAX_MONTHS) {
    m += 1;
    const paid = {};
    ds.forEach((d) => {
      if (d.b > 0.004) { const i = d.b * d.r; d.b += i; interest += i; interestBy[d.id] += i; }
    });
    let pool = n(extra) + freed;
    ds.forEach((d) => {
      if (d.b > 0.004) {
        const p = Math.min(d.min, d.b);
        d.b -= p; paid[d.id] = p;
        pool += d.min - p; // a minimum bigger than what's left spills over
      }
    });
    ds.filter((d) => d.b > 0.004).sort(sorter).forEach((d) => {
      if (pool <= 0.004) return;
      const p = Math.min(pool, d.b);
      d.b -= p; pool -= p; paid[d.id] = (paid[d.id] || 0) + p;
    });
    ds.forEach((d) => {
      if (d.b <= 0.004 && months[d.id] == null) {
        d.b = 0;
        months[d.id] = m;
        if (rollover[d.id] !== false) freed += d.min;
      }
    });
    path.push(ds.reduce((s, d) => s + Math.max(0, d.b), 0));
    payments.push(paid);
  }
  const stuck = ds.some((d) => d.b > 0.004);
  Object.keys(interestBy).forEach((k) => { interestBy[k] = Math.round(interestBy[k] * 100) / 100; });
  return {
    months,
    totalMonths: stuck ? null : m,
    interest: Math.round(interest * 100) / 100,
    interestBy,
    firstMonthPay: payments[0] || {},
    payments,
    path,
    stuck,
  };
}

/** The month n months after the start of `from` (month 1 = this month). */
export const monthAt = (index, from = new Date()) => new Date(from.getFullYear(), from.getMonth() + index - 1, 1, 12);

/** Interest charged on one month's payment, and what comes off what you owe. */
export const splitPayment = (balance, ratePct, amount) => {
  const interest = Math.max(0, n(balance)) * (Math.max(0, n(ratePct)) / 100 / 12);
  const i = Math.round(interest * 100) / 100;
  const principal = Math.max(0, n(amount) - i);
  const left = Math.max(0, Math.round((n(balance) - principal) * 100) / 100);
  return { interest: i, principal: Math.round(principal * 100) / 100, left };
};

/** True when a debt's minimum doesn't even cover its interest. */
export const minTooLow = (d) => n(d.min) <= n(d.balance) * (n(d.rate) / 100 / 12) + 0.004;
